import { ArrowRightIcon } from '@blueprinthq/joy'
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack
} from '@chakra-ui/react'
import React from 'react'

interface ConversionInfo {
  currentPlan: string
  currentSessions: number
  currentFreeCredits: number
  newPlan: string
  convertedSessions: number
  convertedFreeCredits: number
}

interface PurchaseSummaryProps {
  currentPlan: string
  newPlan: string
  sessions: number
  pricePerSession: number
  discount?: number
  autoReplenish?: boolean
  autoReplenishThreshold?: number | null
  autoReplenishCount?: number | null
  onPurchase: () => void
  isSubmitting?: boolean
  isValid?: boolean
  isConversion?: boolean
  conversionInfo?: ConversionInfo | null
}

export const PurchaseSummary = ({
  currentPlan,
  newPlan,
  sessions,
  pricePerSession,
  discount = 0,
  autoReplenish = false,
  autoReplenishThreshold = 5,
  autoReplenishCount = 30,
  onPurchase,
  isSubmitting = false,
  isValid = true,
  isConversion = false,
  conversionInfo
}: PurchaseSummaryProps) => {
  const total = sessions * pricePerSession

  return (
    <Box bg="white" borderRadius="lg" p="6" w="100%">
      <VStack spacing="2" align="stretch">
        <Text fontSize="18px" fontWeight="bold" color="gray.900">
          Summary
        </Text>

        {/* Show "No changes selected" when no sessions and no conversion */}
        {!isConversion && (!sessions || sessions === 0) ? (
          <Flex
            justify="center"
            align="center"
            py="4"
            bg="gray.100"
            borderRadius="md"
          >
            <Text fontSize="14px" color="gray.500" textAlign="center">
              No changes selected
            </Text>
          </Flex>
        ) : (
          <VStack spacing="1" align="stretch">
            {/* Show conversion first if applicable */}
            {isConversion && conversionInfo && (
              <VStack spacing="1" align="stretch">
                <VStack spacing="1" align="stretch" pb="1" pt="1">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="16px" color="gray.900">
                      Session Exchange
                    </Text>
                    <Text fontSize="16px" color="gray.900" fontWeight="regular">
                      $0.00
                    </Text>
                  </Flex>
                  <Flex align="center" gap="1">
                    <Text fontSize="12px" color="gray.600">
                      {conversionInfo.currentSessions +
                        conversionInfo.currentFreeCredits}{' '}
                      {conversionInfo.currentPlan} sessions
                    </Text>
                    <Text fontSize="12px" color="gray.600">
                      →
                    </Text>
                    <Text fontSize="12px" color="gray.600">
                      {conversionInfo.convertedSessions +
                        conversionInfo.convertedFreeCredits}{' '}
                      {conversionInfo.newPlan} sessions
                    </Text>
                  </Flex>
                </VStack>
                <Divider borderColor="gray.300" />
              </VStack>
            )}

            {/* Show additional sessions if applicable */}
            {sessions > 0 && (
              <VStack spacing="1" align="stretch" p="0">
                <VStack spacing="1" align="stretch" pb="1" pt="1">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="16px" color="gray.900">
                      Additional Sessions
                    </Text>
                    <Text fontSize="16px" color="gray.900" fontWeight="regular">
                      ${(sessions * pricePerSession).toFixed(2)}
                    </Text>
                  </Flex>
                  <Text fontSize="12px" color="gray.600">
                    {sessions} {newPlan} sessions
                  </Text>
                </VStack>
                <Divider borderColor="gray.300" />
              </VStack>
            )}
          </VStack>
        )}

        {/* Only show pricing and button when there are changes */}
        {(isConversion || (sessions && sessions > 0)) && (
          <>
            <VStack spacing="1" align="stretch" pb="1" pt="1">
              <Flex justify="space-between" align="center">
                <Text fontSize="16px" fontWeight="bold" color="gray.900">
                  Total
                </Text>
                <Text fontSize="16px" fontWeight="bold" color="gray.900">
                  ${total.toFixed(2)}
                </Text>
              </Flex>
            </VStack>

            <Divider borderColor="gray.300" />

            <Box pt="2">
              <Button
                bg="#2D54E8"
                color="white"
                size="lg"
                width="full"
                borderRadius="8px"
                onClick={onPurchase}
                _hover={{ bg: '#1a3db8' }}
                fontWeight="500"
                isDisabled={!isValid || isSubmitting}
                isLoading={isSubmitting}
                loadingText="Processing..."
              >
                {isSubmitting ? (
                  <Spinner size="sm" />
                ) : isConversion && sessions > 0 ? (
                  'Confirm & Complete Purchase'
                ) : isConversion ? (
                  'Confirm Plan Change'
                ) : (
                  'Complete Purchase'
                )}
              </Button>
            </Box>
          </>
        )}

        {/* Only show auto-replenish and "What happens next" when there are changes */}
        {(isConversion || (sessions && sessions > 0)) && (
          <>
            {autoReplenish && (
              <Box py="2">
                <Text fontSize="12px" color="gray.700">
                  <span style={{ fontWeight: 'bold' }}>Auto-replenish:</span> ON
                </Text>
                <Text fontSize="12px" color="gray.600" mt="1">
                  Purchase {autoReplenishCount} sessions when you have{' '}
                  {autoReplenishThreshold} left
                </Text>
              </Box>
            )}

            <Box mt="2">
              <Text
                fontSize="14px"
                fontWeight="semibold"
                color="gray.700"
                mb="2"
              >
                What happens next:
              </Text>
              <VStack align="stretch" spacing="0.5">
                {/* Conversion bullet point */}
                {isConversion && conversionInfo && (
                  <HStack align="flex-start">
                    <Text fontSize="12px" color="gray.600">
                      •
                    </Text>
                    <Text fontSize="12px" color="gray.600">
                      {conversionInfo.currentSessions +
                        conversionInfo.currentFreeCredits}{' '}
                      {conversionInfo.currentPlan} sessions will be exchanged
                      for{' '}
                      {conversionInfo.convertedSessions +
                        conversionInfo.convertedFreeCredits}{' '}
                      {conversionInfo.newPlan} sessions
                    </Text>
                  </HStack>
                )}

                {/* Additional sessions bullet point */}
                {sessions > 0 && (
                  <HStack align="flex-start">
                    <Text fontSize="12px" color="gray.600">
                      •
                    </Text>
                    <Text fontSize="12px" color="gray.600">
                      {sessions} {newPlan} sessions will be added to your
                      account
                    </Text>
                  </HStack>
                )}
                <HStack align="flex-start">
                  <Text fontSize="12px" color="gray.600">
                    •
                  </Text>
                  <Text fontSize="12px" color="gray.600">
                    You can switch plans anytime
                  </Text>
                </HStack>
                <HStack align="flex-start">
                  <Text fontSize="12px" color="gray.600">
                    •
                  </Text>
                  <Text fontSize="12px" color="gray.600">
                    Sessions never expire
                  </Text>
                </HStack>
              </VStack>
            </Box>
          </>
        )}
      </VStack>
    </Box>
  )
}
